<template>
  <section :class="$style.sectionRoot">
    <header :class="$style.sectionHeader">
      Options
    </header>
    <div :class="$style.sectionContent">
      <div>
        <InputValidator
          :class="$style.timeoutInput"
          :config="{
            attribute: 'timeout',
            label: 'Timeout After',
            name: 'timeout',
            validate: 'required|numeric',
            value: item.timeout,
          }"
          @InputValidatorModel="handleFieldChange"
        />
        <span :class="$style.inlineLabel">
          seconds
        </span>
      </div>

      <div
        v-if="showTimeToLiveSection"
        :class="$style.sectionContent"
      >
        <h3>Time to Live (TTL) Settings</h3>
        <p>
          Commands expire from the queue after 3 days by default. To change the expiration
          period, make a selection or enter a custom duration
        </p>

        <div :class="smartOptionsContentClass">
          <RadioGroup
            v-model="radioButtonSelection"
            name="smartRadioGroup"
            :options="smartDefaultRadioOptions"
          />

          <DropDown
            v-if="showSmartOptions"
            v-model="timeToLive.timeToLiveSmart"
            v-validate="showSmartOptions ? 'required' : ''"
            attribute="timeToLiveSmart"
            :class="$style.launchTypeDropdown"
            name="Options"
            optionLabelKey="label"
            :options="smartOptions"
            optionValueKey="value"
            placeholder="Select"
            @input="timeToLiveChange"
          />
        </div>

        <div :class="customOptionsContentClass">
          <RadioGroup
            v-model="radioButtonSelection"
            name="customRadioGroup"
            :options="customDuratonRadioOptions"
          />
          <div
            v-if="showCustomOptions"
            :class="$style.customInputGroup"
          >
            <div>
              <p :class="$style.timeToLiveAlert">
                Max: {{ maxTimeToLiveDays }} days
              </p>
              <p :class="$style.timeToLiveAlert">
                Min: {{ minTimeToLiveHours }} hour
              </p>
            </div>
            <div>
              <InputValidator
                :class="$style.timeToLiveInput"
                :config="{
                  attribute: 'timeToLiveDays',
                  label: 'Days',
                  name: 'Days',
                  validate: `required|between:0,${maxTimeToLiveDays}|numeric`,
                  value: timeToLive.timeToLiveDays,
                }"
                @InputValidatorModel="timeToLiveChange"
              />
              <InputValidator
                :class="$style.timeToLiveInput"
                :config="{
                  attribute: 'timeToLiveHours',
                  label: 'Hours',
                  name: 'Hours',
                  validate: 'required|numeric|' + setLimitHours,
                  disabled: timeToLive.timeToLiveDays === maxTimeToLiveDays ? true : false,
                  value: timeToLive.timeToLiveHours,
                }"
                @InputValidatorModel="timeToLiveChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { RadioGroup } from '@jumpcloud/ui-components';
import { mapActions, mapGetters, mapState } from 'vuex';
import DropDown from 'components/FormValidation/DropDown.vue';
import InputValidator from 'components/FormValidation/InputValidator.vue';
import launchTypes from 'client/Commands/constants/LaunchTypes';
import timeToLiveLimits from 'client/Commands/constants/TimeToLiveLimits';
import timeToLiveSmartOptions from 'client/Commands/constants/TimeToLiveSmartOptions';

const commandModel = StoreModelMap[ItemTypeConsts.command];

export default {
  name: 'CommandDetailsOptions',

  inject: ['$validator'],

  components: {
    InputValidator,
    RadioGroup,
    DropDown,
  },

  data() {
    return {
      maxTimeToLiveDays: timeToLiveLimits.maxTimeToLiveDays,
      minTimeToLiveHours: timeToLiveLimits.minTimeToLiveHours,
      radioButtonSelection: 'smart',
      smartDefaultRadioOptions: [
        {
          value: 'smart',
          label: 'Use Smart Defaults',
        },
      ],
      customDuratonRadioOptions: [
        {
          value: 'custom',
          label: 'Set Custom Duration',
        },
      ],
      smartOptions: timeToLiveSmartOptions,
    };
  },

  computed: {
    ...mapGetters(commandModel, [
      'timeToLive',
    ]),

    ...mapState(commandModel, [
      'item',
    ]),

    customOptionsContentClass() {
      return this.radioButtonSelection === 'custom' ? this.$style.timeToLiveSectionContent : '';
    },

    /* if days of timeToLive object is 0 the hours must be set not below at the minimum value  */
    setLimitHours() {
      return this.timeToLive.timeToLiveDays === 0 ? `between:${this.minTimeToLiveHours},23` : 'between:0,23';
    },

    showCustomOptions() {
      return this.radioButtonSelection === 'custom';
    },

    showSmartOptions() {
      return this.radioButtonSelection === 'smart';
    },

    showTimeToLiveSection() {
      return this.item.launchType === launchTypes.manual
        || this.item.launchType === launchTypes.trigger;
    },

    smartOptionsContentClass() {
      return this.radioButtonSelection === 'smart' ? this.$style.timeToLiveSectionContent : '';
    },
  },

  created() {
    if (!this.timeToLive.timeToLiveSmart) this.radioButtonSelection = 'custom';
  },

  methods: {
    ...mapActions(commandModel, [
      'setItemField',
      'handleSmartTimeToLiveChange',
      'handleDaysTimeToLiveChange',
      'handleHoursTimeToLiveChange',
    ]),

    handleFieldChange(value, key) {
      this.setItemField({ key, value });
    },

    timeToLiveChange(value, key) {
      switch (key) {
        case 'timeToLiveSmart':
          this.handleSmartTimeToLiveChange({ value });
          break;
        case 'timeToLiveDays':
          this.handleDaysTimeToLiveChange({ value });
          break;
        case 'timeToLiveHours':
          this.handleHoursTimeToLiveChange({ value });
          break;
        default:
          break;
      }
    },
  },
};

</script>

<style module>
@import '../../css/sections.css';

.launchTypeDropdown {
  margin-top: -1rem;
  width: 17rem;
}

.customInputGroup {
  margin-top: -1rem;
}

.timeoutInput {
  display: inline-block;
  vertical-align: top;
  width: 160px;
}

.timeToLiveInput {
  display: inline-block;
  margin-right: 1rem;
  vertical-align: top;
  width: 9rem;
}

.timeToLiveAlert {
  display: inline-block;
  margin-bottom: -1rem;
  margin-right: 1rem;
  vertical-align: top;
  width: 9rem;
}

.inlineLabel {
  margin-left: 6px;
  position: relative;
  top: 34px;
}

.timeToLiveSectionContent {
  margin-bottom: 2rem;
}

</style>
